import { getUserSaveError } from "../utils/userManagementErrors";
import "./UserManagement.css";
import UserManagementIcon from "./UserManagementIcon";
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Box,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const UserFormDialog = ({
  open,
  onClose,
  onSave,
  mode = "create",
  initialData = null,
  roles = [],
  managers = [],
  scopes = [],
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    contactNumber: "",
    password: "",
    confirmPassword: "",
    roleId: "",
    manager_id: "",
    scope_id: "",
    isActive: true,
  });
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const saveErrorRef = useRef(null);
  useEffect(() => { if (open) setSaveError(""); }, [open, mode, initialData]);
  useEffect(() => { if (saveError) saveErrorRef.current?.focus(); }, [saveError]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (open && mode === "edit" && initialData) {
      setFormData({
        firstName: initialData.firstName || "",
        lastName: initialData.lastName || "",
        email: initialData.email || "",
        contactNumber: initialData.contactNumber || "",
        password: "",
        confirmPassword: "",
        roleId: initialData.roleId || "",
        manager_id: initialData.manager_id || initialData.managerId || "",
        scope_id: initialData.scope_id || "",
        isActive: initialData.isActive ?? true   // ✅ ADD
      });
    } else if (open && mode === "create") {
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        contactNumber: "",
        password: "",
        confirmPassword: "",
        roleId: "",
        manager_id: "",
        scope_id: "",
        isActive: true   // ✅ ADD
      });
    }
    setErrors({});
  }, [open, mode, initialData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.contactNumber.trim()) {
      newErrors.contactNumber = 'Contact number is required';
    } else if (!/^\d{10}$/.test(formData.contactNumber.replace(/\D/g, ''))) {
      newErrors.contactNumber = 'Please enter a valid 10-digit phone number';
    }

    // Password validation
    const validatePassword = (pwd) => {
      if (pwd.length < 8) {
        return 'Password must be at least 8 characters long';
      } else if (!/[A-Z]/.test(pwd)) {
        return 'Password must contain at least one uppercase letter';
      } else if (!/[a-z]/.test(pwd)) {
        return 'Password must contain at least one lowercase letter';
      } else if (!/[0-9]/.test(pwd)) {
        return 'Password must contain at least one number';
      } else if (!/[!@#$%^&*]/.test(pwd)) {
        return 'Password must contain at least one special character (!@#$%^&*)';
      }
      return '';
    };

    if (mode === 'create') {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else {
        const passwordError = validatePassword(formData.password);
        if (passwordError) {
          newErrors.password = passwordError;
        }
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    } else {
      // Edit mode - password is optional
      if (formData.password) {
        const passwordError = validatePassword(formData.password);
        if (passwordError) {
          newErrors.password = passwordError;
        }

        // Only validate confirmPassword if password is provided
        if (!formData.confirmPassword) {
          newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const nextState = { ...prev, [name]: value };
      if (name === 'roleId') {
        const selectedRole = roles.find((r) => r.id === value);
        const roleName = selectedRole?.name?.toString().toLowerCase();
        // If role is Super Admin, clear manager (Super Admin has no manager)
        if (roleName === 'super admin' || roleName === 'superadmin') {
          nextState.manager_id = '';
        }
      }
      return nextState;
    });
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSave = async () => {
    if (saving || !validateForm()) return;
    setSaveError("");
    setSaving(true);
    try { await onSave(formData); }
    catch (error) { setSaveError(getUserSaveError(error)); }
    finally { setSaving(false); }
  };

  const handleClose = () => {
    if (saving) return;
    setSaveError("");
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      contactNumber: '',
      password: '',
      confirmPassword: '',
      roleId: '',
      manager_id: '',
      isActive: true   // ✅ ADD
    });
    setErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  return (
    <Dialog
        className={`user-management-dialog ${theme.palette.mode === "dark" ? "user-management-dark" : ""}`}
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          pb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <UserManagementIcon name="user" className="user-management-title-icon" />
        {mode === 'create' ? 'Create User' : 'Edit User'}
        <IconButton
          onClick={handleClose}
          aria-label="Close dialog"
          sx={{
            position: 'relative',
            right: -8,
            top: -8,
            color: 'white',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {saveError && <Alert severity="error" role="alert" tabIndex={-1} ref={saveErrorRef} sx={{mb:2}}>{saveError}</Alert>}
        <Box sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                placeholder="John"
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                error={Boolean(errors.firstName)}
                helperText={errors.firstName}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                placeholder="Doe"
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                error={Boolean(errors.lastName)}
                helperText={errors.lastName}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                placeholder="you@company.com"
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                error={Boolean(errors.email)}
                helperText={errors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contact Number"
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleChange}
                required
                error={Boolean(errors.contactNumber)}
                helperText={errors.contactNumber || 'Enter 10-digit phone number'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                required={mode === 'create'}
                error={Boolean(errors.password)}
                helperText={errors.password || (mode === 'create' ? 'Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char' : 'Leave blank to keep current. Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: theme.palette.primary.main }}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <UserManagementIcon name="eyes" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {(mode === 'create' || (mode === 'edit' && formData.password)) && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required={mode === 'create' || (mode === 'edit' && formData.password)}
                  error={Boolean(errors.confirmPassword)}
                  helperText={errors.confirmPassword}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showConfirmPassword ? "Hide confirmation password" : "Show confirmation password"}
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          sx={{ color: theme.palette.primary.main }}
                        >
                          {showConfirmPassword ? <VisibilityOffIcon /> : <UserManagementIcon name="eyes" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                    MenuProps={{ PaperProps: { className: `user-management-menu ${theme.palette.mode === "dark" ? "user-management-dark" : ""}` } }}
                  name="roleId"
                  value={formData.roleId}
                  label="Role"
                  onChange={handleChange}
                >
                  {roles.map(role => (
                    <MenuItem key={role.id} value={role.id}>
                      <UserManagementIcon name="admin" /> {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Scope</InputLabel>
                <Select
                    MenuProps={{ PaperProps: { className: `user-management-menu ${theme.palette.mode === "dark" ? "user-management-dark" : ""}` } }}
                  name="scope_id"
                  value={formData.scope_id}
                  label="Scope"
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, scope_id: e.target.value }));
                  }}
                >
                  <MenuItem value="">Use Role Scope</MenuItem>
                  {scopes.map((scope) => (
                    <MenuItem key={scope.id} value={scope.id}>
                      {scope.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {(() => {
              const selectedRole = roles.find((r) => r.id === formData.roleId);
              const roleName = selectedRole?.name?.toString().toLowerCase();
              // Hide/disable manager selection only for Super Admin
              if (roleName === 'super admin' || roleName === 'superadmin') {
                return null;
              }
              return (
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    componentsProps={{ popper: { className: `user-management-menu ${theme.palette.mode === "dark" ? "user-management-dark" : ""}` } }}
                    options={managers || []}
                    getOptionLabel={(option) => option.fullName || `${option.firstName || ''} ${option.lastName || ''}`.trim()}
                    value={managers.find((m) => m.userId === formData.manager_id) || null}
                    onChange={(event, newValue) => {
                      setFormData(prev => ({ ...prev, manager_id: newValue ? newValue.userId : '' }));
                    }}
                    isOptionEqualToValue={(option, value) => option?.userId === value?.userId}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Manager"
                        placeholder="Select manager"
                      />
                    )}
                  />
                </Grid>
              );
            })()}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                    MenuProps={{ PaperProps: { className: `user-management-menu ${theme.palette.mode === "dark" ? "user-management-dark" : ""}` } }}
                  name="isActive"
                  value={formData.isActive}
                  label="Status"
                  onChange={(e) =>
                    setFormData(prev => ({
                      ...prev,
                      isActive: e.target.value
                    }))
                  }
                >
                  <MenuItem value={true}><span className="user-management-status-dot is-active"/>Active</MenuItem>
                  <MenuItem value={false}><span className="user-management-status-dot is-inactive"/>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>
        <Button onClick={handleClose} color="inherit" disabled={saving}>
          <UserManagementIcon name="cancel" /> Cancel
        </Button>
        <Button
          disabled={saving}
          onClick={handleSave}
          variant="contained"
          sx={{
            bgcolor: theme.palette.primary.main,
            '&:hover': { bgcolor: theme.palette.primary.dark },
          }}
        >
          <UserManagementIcon name="tick" /> {saving ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserFormDialog;
